import { useContext, useEffect, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { isEmpty, sortBy } from 'lodash';
import dayjs from 'dayjs';
import { useProfitPoolEvolutionContext } from '../context/ProfitPoolEvolutionContext';
import { getPeriodLabel } from '../utils/ppevolutionUtils';

const useDepletionvsMarginData = () => {
    const [depletionVsMarginData, setDepletionVsMarginData] = useState<any[]>([]);
    const [minMarginPercent, setMinMarginPercent] = useState<number>(0);
    const [maxMarginPercent, setMaxMarginPercent] = useState<number>(0);
    const [minDepletionVolume, setMinDepletionVolume] = useState<number>(0);
    const [maxDepletionVolume, setMaxDepletionVolume] = useState<number>(0);

    const { evolutionFrequency } = useProfitPoolEvolutionContext();
    const {
        multiSelectedTiers,
        profitPoolTierList,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);

    const {
        data: volumeEquivalentData,
        isPending: isPendingVolumeEquivalent,
        isError: isErrorVolumeEquivalent,
        isSuccess: isSuccessVolumeEquivalent,
        fetchNextPage: fetchNextPageVolumeEquivalent,
        hasNextPage: hasNextPageVolumeEquivalent,
        isFetchingNextPage: isFetchingNextPageVolumeEquivalent,
    } = useInfiniteQuery({
        queryKey: [
            'volume-equivalent-evolution',
            multiSelectedTiers.join(','),
            idForRestOfComponent,
            timePeriodSelection,
            evolutionFrequency,
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter: { dataKey: '', dimension: '' },
                kpi: 'volume_sales_equivalent',
                tierList: multiSelectedTiers,
                yearAgo: false,
                groupedByList: ['period'],
                dimIdsFilter: idForRestOfComponent,
                frequency: evolutionFrequency,
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                !idForRestOfComponentLoading &&
                timePeriodSelection,
        ),
    });

    const { data, isPending, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: [
            'margin-percentage-evolution',
            multiSelectedTiers.join(','),
            idForRestOfComponent,
            timePeriodSelection,
            evolutionFrequency,
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter: { dataKey: '', dimension: '' },
                kpi: 'margin_percent',
                tierList: multiSelectedTiers,
                yearAgo: false,
                groupedByList: ['period'],
                dimIdsFilter: idForRestOfComponent,
                frequency: evolutionFrequency,
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                !idForRestOfComponentLoading &&
                timePeriodSelection,
        ),
    });

    const fetchCombinedNextPage = () => {
        fetchNextPage();
        fetchNextPageVolumeEquivalent();
    };

    useEffect(() => {
        if (!isPending && !isPendingVolumeEquivalent && !isFetchingNextPage && !isFetchingNextPageVolumeEquivalent) {
            if (
                !isEmpty(data?.pages) &&
                data?.pages &&
                !isEmpty(volumeEquivalentData?.pages) &&
                volumeEquivalentData?.pages
            ) {
                const allData = data.pages.flatMap((page: any) => page?.data || []);
                const sortedData = sortBy(allData, 'period');
                const allVolumeEquivalentData = volumeEquivalentData.pages.flatMap((page: any) => page?.data || []);
                let minMarginPercent = Number.POSITIVE_INFINITY;
                let maxMarginPercent = Number.NEGATIVE_INFINITY;

                let minDepletionVolume = Number.POSITIVE_INFINITY;
                let maxDepletionVolume = Number.NEGATIVE_INFINITY;
                const finalChartData = sortedData.map((item: any) => {
                    // Add random value in the range 0 to 15000 to currentMarginPercent for demo/mock
                    // const currentMarginPercent = Math.floor(Math.random() * 15001);
                    const currentMarginPercent = item.kpi_values?.margin_percentage || 0;
                    if (currentMarginPercent < minMarginPercent) minMarginPercent = currentMarginPercent;
                    if (currentMarginPercent > maxMarginPercent) maxMarginPercent = currentMarginPercent;

                    const volumeSalesEquivalent =
                        allVolumeEquivalentData.find(
                            (volumeEquivalentItem: any) => volumeEquivalentItem.period === item.period,
                        )?.kpi_values?.volume_sales_equivalent || 0;
                    if (volumeSalesEquivalent < minDepletionVolume) minDepletionVolume = volumeSalesEquivalent;
                    if (volumeSalesEquivalent > maxDepletionVolume) maxDepletionVolume = volumeSalesEquivalent;
                    return {
                        // For the same period, distribute tier values equally between 'CBI' and 'Distributor'
                        // tier: Math.floor(Math.random() * 2) === 0 ? 'CBBD' : 'Distributor',

                        tier: profitPoolTierList.find((t: any) => t.tierReference === item.tier)?.tierName,
                        period: item.period,
                        periodLabel: getPeriodLabel(item.period),
                        marginPercent: currentMarginPercent || 0,
                        volume_sales_equivalent: volumeSalesEquivalent || 0,
                        tierReference: item.tier,
                    };
                });
                setDepletionVsMarginData(finalChartData);
                setMinMarginPercent(minMarginPercent === Number.POSITIVE_INFINITY ? 0 : minMarginPercent);
                setMaxMarginPercent(maxMarginPercent === Number.NEGATIVE_INFINITY ? 0 : maxMarginPercent);
                setMinDepletionVolume(minDepletionVolume === Number.POSITIVE_INFINITY ? 0 : minDepletionVolume);
                setMaxDepletionVolume(maxDepletionVolume === Number.NEGATIVE_INFINITY ? 0 : maxDepletionVolume);
            }
        }
    }, [
        data,
        isPending,
        volumeEquivalentData,
        isPendingVolumeEquivalent,
        isFetchingNextPage,
        isFetchingNextPageVolumeEquivalent,
    ]);
    return {
        data: depletionVsMarginData,
        isPending: isPending || isPendingVolumeEquivalent,
        isError: isError || isErrorVolumeEquivalent,
        isSuccess: isSuccess || isSuccessVolumeEquivalent,
        fetchNextPage: fetchCombinedNextPage,
        hasNextPage: hasNextPage || hasNextPageVolumeEquivalent,
        isFetchingNextPage: isFetchingNextPage || isFetchingNextPageVolumeEquivalent,
        minMarginPercent,
        maxMarginPercent,
        minDepletionVolume,
        maxDepletionVolume,
    };
};

export default useDepletionvsMarginData;
